import React, { useState } from 'react';
import { Plus, TrendingUp, TrendingDown, Search, X } from 'lucide-react';
import { WatchlistItem } from '../types';
import { LineChart, Line, ResponsiveContainer } from 'recharts';

interface SidebarProps {
  watchlist: WatchlistItem[];
  selectedStock: string;
  onStockSelect: (symbol: string) => void;
  onAddStock: (symbol: string) => void;
  onRemoveStock: (symbol: string) => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  watchlist,
  selectedStock,
  onStockSelect,
  onAddStock,
  onRemoveStock
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newSymbol, setNewSymbol] = useState('');

  const handleAddStock = (e: React.FormEvent) => {
    e.preventDefault();
    if (newSymbol.trim()) {
      onAddStock(newSymbol.toUpperCase().trim());
      setNewSymbol('');
      setShowAddForm(false);
    }
  };

  const formatSparklineData = (data: number[]) => {
    return data.map((value, index) => ({ index, value }));
  };

  return (
    <div className="w-80 bg-terminal-surface border-r border-terminal-border h-screen flex flex-col">
      {/* Header */}
      <div className="p-6 border-b border-terminal-border">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-terminal-text">Watchlist</h2>
          <button
            onClick={() => setShowAddForm(true)}
            className="p-2 rounded-lg bg-neon-blue/20 hover:bg-neon-blue/30 text-neon-blue transition-colors"
          >
            <Plus size={18} />
          </button>
        </div>

        {/* Add Stock Form */}
        {showAddForm && (
          <form onSubmit={handleAddStock} className="mb-4">
            <div className="flex gap-2">
              <input
                type="text"
                value={newSymbol}
                onChange={(e) => setNewSymbol(e.target.value)}
                placeholder="Enter symbol (e.g., AAPL)"
                className="flex-1 px-3 py-2 bg-terminal-bg border border-terminal-border rounded-lg text-terminal-text placeholder-terminal-muted focus:outline-none focus:border-neon-blue"
                autoFocus
              />
              <button
                type="submit"
                className="px-4 py-2 bg-neon-green/20 hover:bg-neon-green/30 text-neon-green rounded-lg transition-colors"
              >
                Add
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false);
                  setNewSymbol('');
                }}
                className="px-3 py-2 bg-terminal-border hover:bg-terminal-muted/20 text-terminal-muted rounded-lg transition-colors"
              >
                <X size={16} />
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Watchlist Items */}
      <div className="flex-1 overflow-y-auto scrollbar-thin">
        <div className="p-4 space-y-2">
          {watchlist.map((stock) => (
            <div
              key={stock.symbol}
              onClick={() => onStockSelect(stock.symbol)}
              className={`p-4 rounded-lg cursor-pointer transition-all duration-200 border ${
                selectedStock === stock.symbol
                  ? 'bg-neon-blue/10 border-neon-blue/50 neon-glow'
                  : 'bg-terminal-bg border-terminal-border hover:bg-terminal-border/50'
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="font-semibold text-terminal-text">{stock.symbol}</h3>
                  <p className="text-xs text-terminal-muted truncate max-w-[120px]">
                    {stock.name}
                  </p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveStock(stock.symbol);
                  }}
                  className="opacity-0 group-hover:opacity-100 p-1 hover:bg-neon-red/20 text-neon-red rounded transition-all"
                >
                  <X size={14} />
                </button>
              </div>

              <div className="flex items-center justify-between mb-3">
                <span className="text-lg font-bold text-terminal-text">
                  ${stock.price.toFixed(2)}
                </span>
                <div className={`flex items-center gap-1 ${
                  stock.change >= 0 ? 'text-neon-green' : 'text-neon-red'
                }`}>
                  {stock.change >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                  <span className="text-sm font-medium">
                    {stock.changePercent >= 0 ? '+' : ''}{stock.changePercent.toFixed(2)}%
                  </span>
                </div>
              </div>

              {/* Mini Sparkline Chart */}
              <div className="h-8 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={formatSparklineData(stock.sparklineData)}>
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke={stock.change >= 0 ? '#00ff88' : '#ff4757'}
                      strokeWidth={1.5}
                      dot={false}
                      activeDot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="flex justify-between text-xs text-terminal-muted mt-2">
                <span>Vol: {(stock.volume / 1000000).toFixed(1)}M</span>
                <span className={stock.change >= 0 ? 'text-neon-green' : 'text-neon-red'}>
                  {stock.change >= 0 ? '+' : ''}${stock.change.toFixed(2)}
                </span>
              </div>
            </div>
          ))}
        </div>

        {watchlist.length === 0 && (
          <div className="p-8 text-center">
            <Search className="mx-auto mb-4 text-terminal-muted" size={48} />
            <p className="text-terminal-muted mb-2">No stocks in watchlist</p>
            <p className="text-sm text-terminal-muted/70">
              Click the + button to add stocks
            </p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-terminal-border">
        <div className="text-xs text-terminal-muted text-center">
          <p>Market data delayed by 15 minutes</p>
          <p className="mt-1">© 2024 Stock Terminal</p>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;